import { useEffect, useId, useState } from 'react';
import type { DeploymentOutcome, DeploymentStage } from '../../api';
import { plural } from '../../labels';
import { Icon } from '../primitives';
import { StepList, StepRow, type StepItem } from '../primitives/Steps';
import { formatDuration, StepTime } from '../runTiming';
import { groupStages, runDuration, runLabel, runOpensFor, type StageRun } from '../operation/stageGroups';

/**
 * StageList: a run's steps as at most 14 stages (Prepare, Build, Switch
 * traffic, ...), each with its own status and duration, its steps behind
 * it. The steps are the server's own: a stage only groups them, so a
 * failure is still reported on the step that failed.
 *
 * The stages that need attention (failed, running, warned) are rows of
 * their own and the failed or running one is open. Stages that succeeded,
 * were skipped or did not run fold into one line each — "9 stages
 * succeeded" — that opens to every one of them (UX_ARCHITECTURE.md, rule E).
 */
export function StageList({
  stages,
  stepItem,
  now,
  label,
  testIdPrefix = 'stage',
  targetKey,
  outcome = 'deploying',
}: {
  stages: DeploymentStage[];
  /** The step's row, by step key (undefined for a step the list does not know). */
  stepItem: (stepKey: string) => StepItem | undefined;
  now: number;
  label: string;
  testIdPrefix?: string;
  /** A step a summary pointed at (CO-150): its stage opens, even inside a folded line. */
  targetKey?: string | null;
  /** Words the folded lines: "not run after the failure" or "after the cancellation". */
  outcome?: DeploymentOutcome;
}) {
  const toItem = (stage: DeploymentStage, number: number): StepItem => {
    const steps = stage.step_keys.map(stepItem).filter((item): item is StepItem => Boolean(item));
    return {
      key: stage.key,
      number,
      title: stage.title,
      description: stageLine(stage),
      status: { kind: 'stage', value: stage.status },
      meta: <StepTime step={stage} now={now} />,
      details: steps.length > 0 ? <StepList steps={steps} label={`${stage.title}: steps`} /> : undefined,
      open: stage.status === 'failed' || stage.status === 'running' || Boolean(targetKey && stage.step_keys.includes(targetKey)),
      testId: `${testIdPrefix}-${stage.key}`,
    };
  };
  const runs = groupStages(stages);
  return (
    <ol className="step-list" aria-label={label}>
      {runs.map((run) =>
        run.kind === 'attention' ? (
          <StepRow key={run.stages[0]!.key} step={toItem(run.stages[0]!, run.firstNumber)} index={run.firstNumber - 1} numbered />
        ) : (
          <FoldedRun key={run.stages[0]!.key} run={run} outcome={outcome} targetKey={targetKey} toItem={toItem} testIdPrefix={testIdPrefix} />
        ),
      )}
    </ol>
  );
}

/** One line for stages that need no attention; it opens to every stage it holds. */
function FoldedRun({
  run,
  outcome,
  targetKey,
  toItem,
  testIdPrefix,
}: {
  run: StageRun;
  outcome: DeploymentOutcome;
  targetKey?: string | null;
  toItem: (stage: DeploymentStage, number: number) => StepItem;
  testIdPrefix: string;
}) {
  const [open, setOpen] = useState(runOpensFor(run, targetKey));
  const panelId = useId();
  useEffect(() => {
    if (runOpensFor(run, targetKey)) setOpen(true);
  }, [run, targetKey]);
  const spent = runDuration(run);
  const icon = run.kind === 'succeeded' ? 'check-circle' : run.kind === 'waiting' ? 'clock' : 'minus';
  return (
    <li className={`stage-fold stage-fold--${run.kind}`} data-testid={`${testIdPrefix}-fold-${run.stages[0]!.key}`}>
      <button type="button" className="stage-fold__toggle" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((value) => !value)}>
        <Icon name={icon} size={16} className="stage-fold__icon" />
        <span className="stage-fold__label">
          {runLabel(run, outcome)}
          {run.stages.length > 1 ? <span className="stage-fold__range"> (stages {run.firstNumber}–{run.firstNumber + run.stages.length - 1})</span> : <span className="stage-fold__range"> (stage {run.firstNumber}: {run.stages[0]!.title})</span>}
        </span>
        {spent > 0 ? <span className="stage-fold__meta tabular-nums">{formatDuration(spent)}</span> : null}
        <span className="stage-fold__action">
          {open ? 'Hide details' : 'View details'}
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
        </span>
      </button>
      {open ? (
        <ol id={panelId} className="step-list stage-fold__stages" aria-label={`${runLabel(run, outcome)}: stages`}>
          {run.stages.map((stage, index) => (
            <StepRow key={stage.key} step={toItem(stage, run.firstNumber + index)} index={run.firstNumber + index - 1} numbered />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/** One line under a stage: what it is doing, what stopped it, or its size. */
function stageLine(stage: DeploymentStage): string {
  const count = plural(stage.steps, 'step');
  switch (stage.status) {
    case 'running':
      return stage.current_step ? `${stage.done} of ${count} done. Now: ${stage.current_step}` : `${stage.done} of ${count} done`;
    case 'failed':
      return stage.failed_step ? `Stopped at: ${stage.failed_step}` : `Stopped after ${stage.done} of ${count}`;
    case 'warning':
      return stage.failed_step ? `${count}; a check that does not stop the run failed: ${stage.failed_step}` : count;
    default:
      return count;
  }
}
